// src/pages/notifications/NotificationCreate.js - Actualizado para PushNotificationService con tokens activos
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Chip,
  FormHelperText,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Autocomplete,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Radio,
  RadioGroup,
  Collapse,
  IconButton,
  Tooltip,
  LinearProgress
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { 
  Save as SaveIcon, 
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Smartphone,
  Group,
  Person,
  School,
  Info,
  Notifications as NotificationsIcon,
  Send as SendIcon,
  Science as ScienceIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
  Computer as ComputerIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  CleaningServices as CleaningIcon,
  Update as UpdateIcon
} from '@mui/icons-material';
import { 
  collection, 
  getDocs, 
  query, 
  where,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// ✅ IMPORTAR EL SERVICIO DE NOTIFICACIONES PUSH ACTUALIZADO
import pushNotificationService from '../../pages/services/PushNotificationService';

// Tema personalizado (mantenido igual)
const colorPalette = {
  primary: '#9CDBA6',
  primaryDark: '#7FB069',
  primaryLight: '#B8E5C1',
  secondary: '#9C8AC7',
  secondaryDark: '#8A7AB8',
  secondaryLight: '#B5A8D6',
  accent: '#A8E6CF',
  success: '#81C784',
  warning: '#FFB74D',
  error: '#E57373',
  background: '#F8FDF9',
  surface: '#FFFFFF',
  text: '#2E3B3A',
  textSecondary: '#6B7B7A'
};

const customTheme = createTheme({
  palette: {
    primary: {
      main: colorPalette.primary,
      dark: colorPalette.primaryDark,
      light: colorPalette.primaryLight,
    },
    secondary: {
      main: colorPalette.secondary,
      dark: colorPalette.secondaryDark,
      light: colorPalette.secondaryLight,
    },
    background: {
      default: colorPalette.background,
      paper: colorPalette.surface,
    },
    text: {
      primary: colorPalette.text,
      secondary: colorPalette.textSecondary,
    },
    success: { main: colorPalette.success },
    warning: { main: colorPalette.warning },
    error: { main: colorPalette.error },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: { fontWeight: 600, color: colorPalette.text },
    h6: { fontWeight: 500, color: colorPalette.text },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colorPalette.surface,
          boxShadow: '0 4px 12px rgba(156, 219, 166, 0.15)',
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0 4px 12px rgba(156, 138, 199, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(156, 138, 199, 0.4)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(127, 176, 105, 0.12)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colorPalette.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colorPalette.secondary,
            },
          },
        },
      },
    },
  },
});

const NotificationCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sendingProgress, setSendingProgress] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const [sendStats, setSendStats] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [userDevices, setUserDevices] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [tokenStats, setTokenStats] = useState(null);
  const [cleanupInProgress, setCleanupInProgress] = useState(false);

  const [formData, setFormData] = useState({
    título: '',
    descripción: '',
    tipo: 'general',
    fecha: new Date(),
    cursos: [],
    enlaces: [],
    adjuntos: [],
    visible: true,
    actionUrl: '',
    category: '',
    channelId: 'default',
    color: '#9CDBA6',
    icon: ''
  });

  const [filters, setFilters] = useState({
    targetType: 'all',
    specificUserId: '',
    specificDeviceId: '',
    platform: '',
    courses: [],
    useConcurrent: false // ✅ NUEVO: Opción para envío concurrente
  });

  const [enlaceInput, setEnlaceInput] = useState({ nombre: '', url: '' });
  const [cursoInput, setCursoInput] = useState('');
  const [formErrors, setFormErrors] = useState({});

  // ✅ FUNCIÓN ACTUALIZADA - Obtener estadísticas de tokens ACTIVOS
  const getActiveTokensStats = async () => {
    try {
      console.log('🔄 Obteniendo estadísticas de tokens ACTIVOS...');
      
      const tokens = await pushNotificationService.getAllActiveTokens(db);
      const stats = pushNotificationService.calculatePlatformBreakdown(tokens);
      
      return {
        total: tokens.length,
        uniqueUsers: new Set(tokens.map(t => t.userId)).size,
        ...stats,
        tokensDetailed: tokens // Para debugging
      };
    } catch (error) {
      console.error('❌ Error obteniendo estadísticas de tokens activos:', error);
      return {
        total: 0,
        uniqueUsers: 0,
        android: 0,
        ios: 0,
        web: 0,
        other: 0
      };
    }
  };

  // ✅ FUNCIÓN ACTUALIZADA - Obtener usuarios por cursos (solo con tokens activos)
  const getUsersByCourses = async (selectedCourses) => {
    try {
      console.log('🔄 Obteniendo usuarios por cursos (solo tokens activos)...');
      
      if (!selectedCourses || selectedCourses.length === 0) {
        return [];
      }

      const tokens = await pushNotificationService.getTokensForCourseUsers(db, selectedCourses);
      
      // Convertir a formato esperado por la UI
      const uniqueUsers = new Map();
      tokens.forEach(tokenData => {
        if (!uniqueUsers.has(tokenData.userId)) {
          uniqueUsers.set(tokenData.userId, {
            id: tokenData.userId,
            name: tokenData.userData.name,
            email: tokenData.userData.email,
            platforms: [],
            activeTokensCount: 0
          });
        }
        const user = uniqueUsers.get(tokenData.userId);
        if (!user.platforms.includes(tokenData.platform)) {
          user.platforms.push(tokenData.platform);
        }
        user.activeTokensCount++;
      });

      return Array.from(uniqueUsers.values());
    } catch (error) {
      console.error('❌ Error obteniendo usuarios por cursos:', error);
      return [];
    }
  };

  // ✅ FUNCIÓN ACTUALIZADA - Obtener todos los usuarios activos (solo con tokens activos)
  const getAllActiveUsers = async () => {
    try {
      console.log('🔄 Obteniendo usuarios con tokens ACTIVOS...');
      
      const tokens = await pushNotificationService.getAllActiveTokens(db);
      
      // Convertir a formato esperado por la UI
      const uniqueUsers = new Map();
      tokens.forEach(tokenData => {
        if (!uniqueUsers.has(tokenData.userId)) {
          uniqueUsers.set(tokenData.userId, {
            id: tokenData.userId,
            name: tokenData.userData.name,
            email: tokenData.userData.email,
            platforms: [],
            activeTokensCount: 0
          });
        }
        const user = uniqueUsers.get(tokenData.userId);
        if (!user.platforms.includes(tokenData.platform)) {
          user.platforms.push(tokenData.platform);
        }
        user.activeTokensCount++;
      });

      return Array.from(uniqueUsers.values());
    } catch (error) {
      console.error('❌ Error obteniendo usuarios activos:', error);
      return [];
    }
  };

  // ✅ FUNCIÓN ACTUALIZADA - Obtener dispositivos (solo activos)
  const getUserDevices = async (userId) => {
    try {
      console.log('🔄 Obteniendo dispositivos ACTIVOS del usuario...');
      
      const tokens = await pushNotificationService.getTokensForUsers(db, [userId]);
      
      return tokens.map((tokenData, index) => ({
        id: `${userId}_${index}`,
        token: tokenData.token,
        displayName: `${tokenData.deviceInfo?.deviceName || 'Dispositivo'} (${tokenData.platform})`,
        platform: tokenData.platform,
        deviceInfo: tokenData.deviceInfo,
        active: tokenData.active,
        lastUpdated: tokenData.lastUpdated
      }));
    } catch (error) {
      console.error('❌ Error obteniendo dispositivos activos del usuario:', error);
      return [];
    }
  };

  // ✅ NUEVA FUNCIÓN - Limpiar tokens inactivos
  const handleCleanupInactiveTokens = async () => {
    try {
      setCleanupInProgress(true);
      console.log('🧹 Iniciando limpieza de tokens inactivos...');
      
      const result = await pushNotificationService.cleanupAllInactiveTokens(db);
      
      if (result.success) {
        console.log('✅ Limpieza completada:', result);
        
        // Actualizar estadísticas
        const newStats = await getActiveTokensStats();
        setTokenStats(newStats);
        
        // Mostrar resultado
        alert(`Limpieza completada:\n- ${result.totalTokensRemoved} tokens eliminados\n- ${result.totalTokensKept} tokens mantenidos\n- ${result.usersProcessed}/${result.totalUsers} usuarios procesados`);
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Error en limpieza:', error);
      setError(`Error limpiando tokens: ${error.message}`);
    } finally {
      setCleanupInProgress(false);
    }
  };

  // ✅ FUNCIÓN ACTUALIZADA - Crear y enviar notificación
  const createAndSendNotification = async (notificationData, filtersData) => {
    try {
      console.log('🚀 Creando y enviando notificación usando PushNotificationService (solo tokens activos)...');
      
      // 1. Crear el documento de notificación en Firestore
      const notificationDoc = {
        título: notificationData.título,
        descripción: notificationData.descripción,
        tipo: notificationData.tipo,
        fecha: notificationData.fecha,
        cursos: notificationData.cursos,
        enlaces: notificationData.enlaces,
        adjuntos: notificationData.adjuntos,
        visible: notificationData.visible,
        actionUrl: notificationData.actionUrl,
        category: notificationData.category,
        channelId: notificationData.channelId,
        color: notificationData.color,
        icon: notificationData.icon,
        createdAt: serverTimestamp(),
        filters: filtersData,
        status: 'sending',
        onlyActiveTokens: true // ✅ NUEVO: Marcar que solo usa tokens activos
      };

      console.log('📝 Guardando notificación en Firestore...');
      const docRef = await addDoc(collection(db, 'notifications'), notificationDoc);
      console.log('✅ Notificación guardada con ID:', docRef.id);

      // 2. Preparar datos para el servicio de push notifications
      const pushNotificationData = {
        id: docRef.id,
        título: notificationData.título,
        descripción: notificationData.descripción,
        tipo: notificationData.tipo,
        cursos: notificationData.cursos,
        enlaces: notificationData.enlaces,
        actionUrl: notificationData.actionUrl,
        category: notificationData.category,
        channelId: notificationData.channelId,
        color: notificationData.color,
        icon: notificationData.icon,
        customData: {
          notificationId: docRef.id,
          createdBy: 'admin',
          timestamp: new Date().toISOString(),
          onlyActiveTokens: true
        }
      };

      // 3. Configurar callback de progreso
      const progressCallback = (progress) => {
        setSendingProgress(Math.min(progress, 90)); // Máximo 90% durante envío
      };

      // 4. Enviar notificaciones push usando el servicio (SOLO TOKENS ACTIVOS)
      console.log('📤 Enviando notificaciones push a tokens ACTIVOS...');
      setSendingProgress(25);
      
      const pushResult = await pushNotificationService.sendPushNotifications(
        pushNotificationData,
        filtersData,
        db,
        progressCallback,
        filtersData.useConcurrent || false // Usar envío concurrente si está habilitado
      );

      setSendingProgress(90);

      // 5. Actualizar el documento con los resultados
      if (pushResult.success) {
        console.log('✅ Notificaciones push enviadas exitosamente a tokens ACTIVOS');
        
        await updateDoc(doc(db, 'notifications', docRef.id), {
          status: 'sent',
          pushResults: pushResult,
          sentAt: serverTimestamp(),
          onlyActiveTokensConfirmed: true,
          finalStats: {
            totalActiveTokens: pushResult.totalTokens,
            sentNotifications: pushResult.sentNotifications,
            failedNotifications: pushResult.failedNotifications,
            invalidTokens: pushResult.invalidTokens.length,
            platformBreakdown: pushResult.platformBreakdown,
            sendMode: pushResult.sendMode
          }
        });
      }

      setSendingProgress(100);

      return {
        success: true,
        notificationId: docRef.id,
        onlyActiveTokens: true,
        ...pushResult
      };

    } catch (error) {
      console.error('❌ Error creando y enviando notificación:', error);
      throw error;
    }
  };

  // ✅ FUNCIÓN ACTUALIZADA - Enviar notificación de prueba
  const sendTestNotification = async () => {
    try {
      console.log('🧪 Enviando notificación de prueba (solo tokens activos)...');
      
      const testNotificationData = {
        id: 'test_' + Date.now(),
        título: formData.título || 'Notificación de Prueba',
        descripción: formData.descripción || 'Esta es una notificación de prueba para tokens activos',
        tipo: 'test',
        actionUrl: formData.actionUrl,
        category: formData.category,
        channelId: formData.channelId || 'default',
        color: formData.color,
        customData: {
          isTest: true,
          onlyActiveTokens: true
        }
      };

      const result = await pushNotificationService.sendTestNotification(testNotificationData);
      
      return {
        success: true,
        isTest: true,
        onlyActiveTokens: true,
        targetTokens: result.totalTokens,
        sentNotifications: result.sentNotifications,
        platformBreakdown: result.platformBreakdown || { ios: 1, android: 0, web: 0 },
        result: result
      };
    } catch (error) {
      console.error('❌ Error enviando notificación de prueba:', error);
      throw error;
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Cargar estadísticas de tokens ACTIVOS
      const stats = await getActiveTokensStats();
      setTokenStats(stats);

      // Cargar cursos disponibles
      await loadAvailableCourses();
    } catch (error) {
      console.error('Error cargando datos iniciales:', error);
      setError('Error cargando datos iniciales');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableCourses = async () => {
    try {
      const coursesData = [
        '1º ESO', '2º ESO', '3º ESO', '4º ESO', 
        '1º Bachillerato', '2º Bachillerato',
        'P3', 'P4', 'P5',
        '1º Primaria', '2º Primaria', '3º Primaria', 
        '4º Primaria', '5º Primaria', '6º Primaria'
      ];
      setCourses(coursesData);
    } catch (error) {
      console.error('Error cargando cursos:', error);
    }
  };

  const loadUsersByCourses = async (selectedCourses) => {
    setLoadingUsers(true);
    try {
      const usersData = await getUsersByCourses(selectedCourses);
      setUsers(usersData);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
      setError('Error cargando usuarios por cursos');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadUserDevices = async (userId) => {
    try {
      const devices = await getUserDevices(userId);
      setUserDevices(devices);
    } catch (error) {
      console.error('Error cargando dispositivos:', error);
      setError('Error cargando dispositivos del usuario');
    }
  };

  const loadAllActiveUsers = async () => {
    setLoadingUsers(true);
    try {
      console.log('🔄 Cargando usuarios con tokens activos...');
      const usersData = await getAllActiveUsers();
      console.log('✅ Usuarios con tokens activos cargados:', usersData.length);
      setUsers(usersData);
    } catch (error) {
      console.error('❌ Error cargando usuarios activos:', error);
      setError('Error cargando usuarios activos');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const handleFilterChange = (field, value) => {
    console.log('🎯 Cambio de filtro:', field, '→', value);
    
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);

    // Cargar datos específicos según el filtro
    if (field === 'targetType') {
      if (value === 'course' && formData.cursos.length > 0) {
        console.log('📚 Cargando usuarios por cursos (solo tokens activos)...');
        loadUsersByCourses(formData.cursos);
      }
      
      if (value === 'user') {
        console.log('👥 Cargando usuarios con tokens activos...');
        loadAllActiveUsers();
      }
      
      if (value === 'device' && newFilters.specificUserId) {
        console.log('📱 Cargando dispositivos activos del usuario...');
        loadUserDevices(newFilters.specificUserId);
      }
    }

    if (field === 'specificUserId' && value) {
      console.log('📱 Cargando dispositivos activos para usuario:', value);
      loadUserDevices(value);
    }
  };

  const handleDateChange = (newDate) => {
    setFormData({
      ...formData,
      fecha: newDate || new Date()
    });
  };

  const handleAddEnlace = () => {
    if (!enlaceInput.nombre || !enlaceInput.url) {
      setFormErrors({
        ...formErrors,
        enlaces: 'El nombre y la URL son obligatorios'
      });
      return;
    }

    setFormData({
      ...formData,
      enlaces: [...formData.enlaces, { ...enlaceInput }]
    });
    setEnlaceInput({ nombre: '', url: '' });
    
    if (formErrors.enlaces) {
      setFormErrors({
        ...formErrors,
        enlaces: ''
      });
    }
  };

  const handleRemoveEnlace = (index) => {
    const newEnlaces = [...formData.enlaces];
    newEnlaces.splice(index, 1);
    setFormData({
      ...formData,
      enlaces: newEnlaces
    });
  };

  const handleAddCurso = () => {
    if (!cursoInput) {
      setFormErrors({
        ...formErrors,
        cursos: 'El curso no puede estar vacío'
      });
      return;
    }

    if (!formData.cursos.includes(cursoInput)) {
      const newCursos = [...formData.cursos, cursoInput];
      setFormData({
        ...formData,
        cursos: newCursos
      });

      // Si el filtro es por curso, actualizar usuarios
      if (filters.targetType === 'course') {
        loadUsersByCourses(newCursos);
      }
    }
    setCursoInput('');
    
    if (formErrors.cursos) {
      setFormErrors({
        ...formErrors,
        cursos: ''
      });
    }
  };

  const handleRemoveCurso = (curso) => {
    const newCursos = formData.cursos.filter(c => c !== curso);
    setFormData({
      ...formData,
      cursos: newCursos
    });

    // Si el filtro es por curso, actualizar usuarios
    if (filters.targetType === 'course' && newCursos.length > 0) {
      loadUsersByCourses(newCursos);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.título.trim()) {
      errors.título = 'El título es obligatorio';
    }
    
    if (!formData.descripción.trim()) {
      errors.descripción = 'La descripción es obligatoria';
    }
    
    if (!formData.tipo) {
      errors.tipo = 'El tipo es obligatorio';
    }
    
    if (formData.tipo === 'curso' && formData.cursos.length === 0) {
      errors.cursos = 'Debe seleccionar al menos un curso';
    }

    if (filters.targetType === 'user' && !filters.specificUserId) {
      errors.filters = 'Debe seleccionar un usuario específico';
    }

    if (filters.targetType === 'device' && !filters.specificDeviceId) {
      errors.filters = 'Debe seleccionar un dispositivo específico';
    }

    if (formData.actionUrl && !formData.actionUrl.startsWith('http')) {
      errors.actionUrl = 'La URL de acción debe ser válida (comenzar con http/https)';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getTargetSummary = () => {
    switch (filters.targetType) {
      case 'all':
        return `Todos los usuarios con tokens activos (${tokenStats?.uniqueUsers || 0} usuarios, ${tokenStats?.total || 0} dispositivos)`;
      case 'course':
        return `Usuarios con tokens activos en los cursos seleccionados (${users.length} usuarios)`;
      case 'user':
        const selectedUser = users.find(u => u.id === filters.specificUserId);
        return selectedUser ? `Usuario con tokens activos: ${selectedUser.name || selectedUser.email} (${selectedUser.activeTokensCount} dispositivos)` : 'Seleccionar usuario';
      case 'device':
        const selectedDevice = userDevices.find(d => d.id === filters.specificDeviceId);
        return selectedDevice ? `Dispositivo activo: ${selectedDevice.displayName}` : 'Seleccionar dispositivo';
      default:
        return 'Sin filtro definido';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    setSendingProgress(0);
    
    try {
      // Preparar filtros finales
      const finalFilters = {
        ...filters,
        courses: formData.cursos
      };

      console.log('📝 Datos del formulario:', formData);
      console.log('🎯 Filtros finales (solo tokens activos):', finalFilters);

      const result = await createAndSendNotification(formData, finalFilters);
      
      if (result.success) {
        setSendStats(result);
        setShowSuccess(true);
        console.log(`✅ Notificación enviada a ${result.sentNotifications} dispositivos ACTIVOS`);
        
        setTimeout(() => {
          navigate('/admin/notifications');
        }, 6000); // Más tiempo para leer estadísticas
      
      } else {
        setError('Error al enviar las notificaciones');
      }
    } catch (error) {
      console.error('❌ Error creating notification:', error);
      setError(`Error al crear la notificación: ${error.message}`);
    } finally {
      setLoading(false);
      setSendingProgress(0);
    }
  };

  const handleTestNotification = async () => {
    if (!formData.título || !formData.descripción) {
      setError('Debe completar al menos el título y descripción para enviar una prueba');
      return;
    }

    try {
      setLoading(true);
      const result = await sendTestNotification();
      setSendStats(result);
      setShowSuccess(true);
    } catch (error) {
      setError(`Error enviando notificación de prueba: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'ios':
        return <AppleIcon sx={{ fontSize: 16, color: '#007AFF' }} />;
      case 'android':
        return <AndroidIcon sx={{ fontSize: 16, color: '#34A853' }} />;
      case 'web':
        return <ComputerIcon sx={{ fontSize: 16, color: '#4285F4' }} />;
      default:
        return <Smartphone sx={{ fontSize: 16 }} />;
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
        <Box sx={{ 
          flexGrow: 1, 
          bgcolor: colorPalette.background, 
          minHeight: '100vh',
          p: 3 
        }}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <NotificationsIcon sx={{ fontSize: 32, color: colorPalette.primary }} />
                <Typography variant="h5">Nueva Notificación (Solo Tokens Activos)</Typography>
              </Box>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/admin/notifications')}
                sx={{ color: colorPalette.textSecondary }}
              >
                Volver
              </Button>
            </Box>
            
            {/* ✅ ESTADÍSTICAS MEJORADAS DE TOKENS ACTIVOS */}
            {tokenStats && (
              <Card sx={{ mb: 3, bgcolor: colorPalette.primaryLight }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Info sx={{ color: colorPalette.primary }} />
                      Estado de Dispositivos Activos
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Actualizar estadísticas">
                        <IconButton 
                          onClick={loadInitialData} 
                          size="small"
                          sx={{ color: colorPalette.secondary }}
                        >
                          <UpdateIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Limpiar tokens inactivos de todos los usuarios">
                        <IconButton 
                          onClick={handleCleanupInactiveTokens}
                          disabled={cleanupInProgress}
                          size="small"
                          sx={{ color: colorPalette.warning }}
                        >
                          {cleanupInProgress ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CleaningIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  <Alert 
                    severity="info" 
                    sx={{ mb: 2, bgcolor: colorPalette.accent }}
                  >
                    <Typography variant="body2">
                      ✅ <strong>Solo tokens activos:</strong> Esta versión del sistema únicamente enviará notificaciones a dispositivos con tokens activos y válidos, garantizando mayor efectividad.
                    </Typography>
                  </Alert>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AndroidIcon sx={{ color: '#4CAF50' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Android Activos</Typography>
                          <Typography variant="h6" sx={{ color: colorPalette.text }}>{tokenStats.android}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AppleIcon sx={{ color: '#007AFF' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">iOS Activos</Typography>
                          <Typography variant="h6" sx={{ color: colorPalette.text }}>{tokenStats.ios}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ComputerIcon sx={{ color: '#4285F4' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Web Activos</Typography>
                          <Typography variant="h6" sx={{ color: colorPalette.text }}>{tokenStats.web}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Group sx={{ color: colorPalette.secondary }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">Usuarios Activos</Typography>
                          <Typography variant="h6" sx={{ color: colorPalette.text }}>{tokenStats.uniqueUsers}</Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total de dispositivos activos: <strong>{tokenStats.total}</strong>
                    </Typography>
                    {cleanupInProgress && (
                      <Typography variant="body2" sx={{ color: colorPalette.warning, fontStyle: 'italic' }}>
                        🧹 Limpieza en progreso...
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
            
            {/* ✅ BARRA DE PROGRESO DURANTE EL ENVÍO */}
            {loading && sendingProgress > 0 && (
              <Card sx={{ mb: 3, bgcolor: colorPalette.accent }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SendIcon sx={{ color: colorPalette.secondary }} />
                    Enviando a Dispositivos Activos...
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={sendingProgress} 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      bgcolor: colorPalette.surface,
                      '& .MuiLinearProgress-bar': {
                        bgcolor: colorPalette.secondary
                      }
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Progreso: {sendingProgress}% - Solo enviando a tokens activos
                  </Typography>
                </CardContent>
              </Card>
            )}
            
            <Divider sx={{ mb: 3 }} />
            
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Información básica de la notificación */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Información de la Notificación
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Título"
                    name="título"
                    value={formData.título}
                    onChange={handleChange}
                    error={!!formErrors.título}
                    helperText={formErrors.título}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descripción"
                    name="descripción"
                    value={formData.descripción}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    error={!!formErrors.descripción}
                    helperText={formErrors.descripción}
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!formErrors.tipo}>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      name="tipo"
                      value={formData.tipo}
                      onChange={handleChange}
                      label="Tipo"
                    >
                      <MenuItem value="general">General</MenuItem>
                      <MenuItem value="urgent">Urgente</MenuItem>
                      <MenuItem value="reminder">Recordatorio</MenuItem>
                      <MenuItem value="info">Informativo</MenuItem>
                      <MenuItem value="marketing">Marketing</MenuItem>
                      <MenuItem value="academic">Académico</MenuItem>
                      <MenuItem value="activities">Actividades</MenuItem>
                      <MenuItem value="shop">Tienda</MenuItem>
                      <MenuItem value="curso">Específico del curso</MenuItem>
                    </Select>
                    {formErrors.tipo && <FormHelperText>{formErrors.tipo}</FormHelperText>}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="Fecha y hora"
                    value={formData.fecha}
                    onChange={handleDateChange}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>

                {/* ✅ CONFIGURACIONES AVANZADAS PARA PUSH NOTIFICATIONS */}
                <Grid item xs={12}>
                  <Card sx={{ mt: 2, bgcolor: colorPalette.background }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <NotificationsIcon sx={{ color: colorPalette.secondary }} />
                        Configuración Avanzada Push
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="URL de Acción (Opcional)"
                            name="actionUrl"
                            value={formData.actionUrl}
                            onChange={handleChange}
                            error={!!formErrors.actionUrl}
                            helperText={formErrors.actionUrl || "URL que se abrirá al tocar la notificación"}
                            placeholder="https://example.com/page"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Canal de Notificación"
                            name="channelId"
                            value={formData.channelId}
                            onChange={handleChange}
                            helperText="Para Android - categorizar notificaciones"
                            placeholder="default"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Categoría iOS (Opcional)"
                            name="category"
                            value={formData.category}
                            onChange={handleChange}
                            helperText="Para acciones personalizadas en iOS"
                            placeholder="GENERAL"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Color (Android)"
                            name="color"
                            value={formData.color}
                            onChange={handleChange}
                            helperText="Color del icono en formato hex"
                            placeholder="#9CDBA6"
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Configuración de destinatarios */}
                <Grid item xs={12}>
                  <Card sx={{ mt: 2, bgcolor: colorPalette.background }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Group sx={{ color: colorPalette.secondary }} />
                          Configuración de Destinatarios (Solo Tokens Activos)
                        </Typography>
                        <IconButton onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
                          {showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {getTargetSummary()}
                      </Typography>

                      <Collapse in={showAdvancedFilters}>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                          <Grid item xs={12}>
                            <FormControl component="fieldset">
                              <Typography variant="subtitle1" gutterBottom>Tipo de Envío</Typography>
                              <RadioGroup
                                value={filters.targetType}
                                onChange={(e) => handleFilterChange('targetType', e.target.value)}
                              >
                                <FormControlLabel 
                                  value="all" 
                                  control={<Radio />} 
                                  label="Todos los usuarios con tokens activos" 
                                />
                                <FormControlLabel 
                                  value="course" 
                                  control={<Radio />} 
                                  label="Usuarios con tokens activos en cursos específicos" 
                                />
                                <FormControlLabel 
                                  value="user" 
                                  control={<Radio />} 
                                  label="Usuario específico (solo si tiene tokens activos)" 
                                />
                                <FormControlLabel 
                                  value="device" 
                                  control={<Radio />} 
                                  label="Dispositivo específico (solo si está activo)" 
                                />
                              </RadioGroup>
                            </FormControl>
                          </Grid>

                          {/* ✅ NUEVA OPCIÓN: Modo de envío */}
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={filters.useConcurrent}
                                  onChange={(e) => handleFilterChange('useConcurrent', e.target.checked)}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: colorPalette.success,
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: colorPalette.success,
                                    },
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">
                                    Envío concurrente (más rápido para muchos dispositivos)
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Recomendado para más de 100 tokens activos
                                  </Typography>
                                </Box>
                              }
                            />
                          </Grid>

                          {/* Filtro por plataforma */}
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                              <InputLabel>Plataforma (Opcional)</InputLabel>
                              <Select
                                value={filters.platform}
                                onChange={(e) => handleFilterChange('platform', e.target.value)}
                                label="Plataforma (Opcional)"
                              >
                                <MenuItem value="">Todas las plataformas activas</MenuItem>
                                <MenuItem value="ios">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AppleIcon sx={{ fontSize: 16 }} />
                                    Solo iOS activos
                                  </Box>
                                </MenuItem>
                                <MenuItem value="android">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AndroidIcon sx={{ fontSize: 16 }} />
                                    Solo Android activos
                                  </Box>
                                </MenuItem>
                                <MenuItem value="web">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ComputerIcon sx={{ fontSize: 16 }} />
                                    Solo Web activos
                                  </Box>
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Filtro por usuario específico */}
                          {filters.targetType === 'user' && (
                            <Grid item xs={12}>
                              <Autocomplete
                                options={users}
                                getOptionLabel={(option) => `${option.name || option.email} (${option.email}) - ${option.activeTokensCount} dispositivos activos`}
                                value={users.find(u => u.id === filters.specificUserId) || null}
                                onChange={(event, newValue) => {
                                  handleFilterChange('specificUserId', newValue ? newValue.id : '');
                                }}
                                loading={loadingUsers}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                      <Person sx={{ fontSize: 16 }} />
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2">
                                          {option.name || option.email}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {option.email} - {option.activeTokensCount} tokens activos
                                        </Typography>
                                        {option.platforms && (
                                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                                            {option.platforms.map((platform, index) => (
                                              <Box key={index}>{getPlatformIcon(platform)}</Box>
                                            ))}
                                          </Box>
                                        )}
                                      </Box>
                                    </Box>
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Seleccionar Usuario (Solo con Tokens Activos)"
                                    error={!!formErrors.filters}
                                    helperText={formErrors.filters}
                                    InputProps={{
                                      ...params.InputProps,
                                      endAdornment: (
                                        <>
                                          {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                                          {params.InputProps.endAdornment}
                                        </>
                                      ),
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                          )}

                          {/* Filtro por dispositivo específico */}
                          {filters.targetType === 'device' && filters.specificUserId && (
                            <Grid item xs={12}>
                              <FormControl fullWidth error={!!formErrors.filters}>
                                <InputLabel>Dispositivo Activo</InputLabel>
                                <Select
                                  value={filters.specificDeviceId}
                                  onChange={(e) => handleFilterChange('specificDeviceId', e.target.value)}
                                  label="Dispositivo Activo"
                                >
                                  {userDevices.map((device) => (
                                    <MenuItem key={device.id} value={device.id}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {getPlatformIcon(device.platform)}
                                        <Box>
                                          <Typography variant="body2">
                                            {device.displayName} ✅
                                          </Typography>
                                          {device.deviceInfo && (
                                            <Typography variant="caption" color="text.secondary">
                                              {device.deviceInfo.deviceName || 'Dispositivo desconocido'} - Token activo
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                                {formErrors.filters && <FormHelperText>{formErrors.filters}</FormHelperText>}
                              </FormControl>
                            </Grid>
                          )}
                        </Grid>
                      </Collapse>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Cursos */}
                {(formData.tipo === 'curso' || filters.targetType === 'course') && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <School sx={{ color: colorPalette.secondary }} />
                      Cursos (Solo usuarios con tokens activos)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Autocomplete
                        options={courses}
                        value={cursoInput}
                        onInputChange={(event, newValue) => setCursoInput(newValue)}
                        freeSolo
                        sx={{ mr: 1, flexGrow: 1 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Añadir curso"
                            error={!!formErrors.cursos}
                            helperText={formErrors.cursos}
                          />
                        )}
                      />
                      <Button 
                        variant="outlined" 
                        onClick={handleAddCurso}
                        sx={{ 
                          color: colorPalette.secondary,
                          borderColor: colorPalette.secondary
                        }}
                      >
                        Añadir
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {formData.cursos.map((curso, index) => (
                        <Chip
                          key={index}
                          label={curso}
                          onDelete={() => handleRemoveCurso(curso)}
                          sx={{ 
                            bgcolor: colorPalette.primaryLight,
                            color: colorPalette.text
                          }}
                        />
                      ))}
                    </Box>
                    
                    {/* Lista de usuarios afectados con tokens activos */}
                    {filters.targetType === 'course' && users.length > 0 && (
                      <Card sx={{ mt: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle2" gutterBottom>
                            Usuarios con tokens activos que recibirán la notificación ({users.length}):
                          </Typography>
                          <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                            {users.slice(0, 10).map((user) => (
                              <ListItem key={user.id}>
                                <ListItemIcon>
                                  <Person fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={`${user.name || user.email} ✅`}
                                  secondary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption">
                                        {user.email} - {user.activeTokensCount} tokens activos
                                      </Typography>
                                      {user.platforms && user.platforms.map((platform, index) => (
                                        <Box key={index}>{getPlatformIcon(platform)}</Box>
                                      ))}
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ))}
                            {users.length > 10 && (
                              <ListItem>
                                <ListItemText 
                                  primary={`... y ${users.length - 10} usuarios más con tokens activos`}
                                  sx={{ fontStyle: 'italic' }}
                                />
                              </ListItem>
                            )}
                          </List>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>
                )}
                
                {/* Enlaces */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Enlaces (Opcional)
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          label="Nombre del enlace"
                          value={enlaceInput.nombre}
                          onChange={(e) => setEnlaceInput({ ...enlaceInput, nombre: e.target.value })}
                          error={!!formErrors.enlaces}
                        />
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <TextField
                          fullWidth
                          label="URL"
                          value={enlaceInput.url}
                          onChange={(e) => setEnlaceInput({ ...enlaceInput, url: e.target.value })}
                          error={!!formErrors.enlaces}
                        />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Button
                          variant="outlined"
                          onClick={handleAddEnlace}
                          fullWidth
                          sx={{ 
                            height: '100%',
                            color: colorPalette.secondary,
                            borderColor: colorPalette.secondary
                          }}
                        >
                          Añadir
                        </Button>
                      </Grid>
                    </Grid>
                    {formErrors.enlaces && (
                      <FormHelperText error>{formErrors.enlaces}</FormHelperText>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {formData.enlaces.map((enlace, index) => (
                      <Chip
                        key={index}
                        label={`${enlace.nombre}: ${enlace.url}`}
                        onDelete={() => handleRemoveEnlace(index)}
                        sx={{ 
                          bgcolor: colorPalette.accent,
                          color: colorPalette.text
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
                
                {/* Configuración adicional */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.visible}
                        onChange={(e) => setFormData({ ...formData, visible: e.target.checked })}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: colorPalette.success,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: colorPalette.success,
                          },
                        }}
                      />
                    }
                    label="Notificación visible para los usuarios"
                  />
                </Grid>
                
                {/* Botones de acción */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/admin/notifications')}
                      disabled={loading}
                      sx={{ 
                        color: colorPalette.textSecondary,
                        borderColor: colorPalette.textSecondary
                      }}
                    >
                      Cancelar
                    </Button>
                    
                    <Tooltip title="Enviar notificación de prueba a tokens activos disponibles">
                      <Button
                        variant="outlined"
                        onClick={handleTestNotification}
                        disabled={loading || !formData.título || !formData.descripción}
                        startIcon={<ScienceIcon />}
                        sx={{ 
                          color: colorPalette.warning,
                          borderColor: colorPalette.warning,
                          '&:hover': {
                            borderColor: colorPalette.warning,
                            bgcolor: `${colorPalette.warning}15`
                          }
                        }}
                      >
                        Prueba (Solo Activos)
                      </Button>
                    </Tooltip>
                    
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      disabled={loading}
                      sx={{ 
                        bgcolor: colorPalette.secondary,
                        '&:hover': { 
                          bgcolor: colorPalette.secondaryDark 
                        }
                      }}
                    >
                      {loading ? 'Enviando...' : 'Enviar a Tokens Activos'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
          
          {/* ✅ SNACKBARS MEJORADOS CON INFORMACIÓN DE TOKENS ACTIVOS */}
          <Snackbar
            open={showSuccess}
            autoHideDuration={10000}
            onClose={() => setShowSuccess(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              severity="success"
              sx={{ 
                bgcolor: colorPalette.success,
                color: 'white',
                '& .MuiAlert-icon': { color: 'white' },
                maxWidth: 600
              }}
            >
              {sendStats ? (
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                    {sendStats.isTest ? 'Notificación de prueba enviada (Solo Tokens Activos)' : 'Notificación enviada exitosamente (Solo Tokens Activos)'}
                  </Typography>
                  
                  {!sendStats.isTest && (
                    <>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        📱 Dispositivos activos alcanzados: {sendStats.sentNotifications || sendStats.pushNotificationsSent}/{sendStats.totalTokens}
                      </Typography>
                      
                      {sendStats.platformBreakdown && (
                        <Typography variant="body2">
                          📊 iOS: {sendStats.platformBreakdown.ios} | Android: {sendStats.platformBreakdown.android} | Web: {sendStats.platformBreakdown.web || 0}
                        </Typography>
                      )}
                      
                      {sendStats.invalidTokens && sendStats.invalidTokens.length > 0 && (
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          ⚠️ Tokens inválidos encontrados y omitidos: {sendStats.invalidTokens.length}
                        </Typography>
                      )}

                      {sendStats.sendMode && (
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          ⚡ Modo de envío: {sendStats.sendMode === 'concurrent' ? 'Concurrente (rápido)' : 'Secuencial (estable)'}
                        </Typography>
                      )}
                    </>
                  )}
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    ✅ Solo se enviaron notificaciones a tokens activos y válidos
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Redirigiendo en unos segundos...
                  </Typography>
                </Box>
              ) : (
                'Notificación procesada exitosamente (Solo Tokens Activos)'
              )}
            </Alert>
          </Snackbar>
          
          <Snackbar
            open={!!error}
            autoHideDuration={8000}
            onClose={() => setError('')}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              severity="error"
              sx={{ 
                bgcolor: colorPalette.error,
                color: 'white',
                '& .MuiAlert-icon': { color: 'white' },
                maxWidth: 500
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon sx={{ fontSize: 20 }} />
                Error al procesar notificación
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                Nota: Solo se procesan tokens activos para mayor efectividad
              </Typography>
            </Alert>
          </Snackbar>

          {/* ✅ SNACKBAR ADICIONAL PARA LIMPIEZA DE TOKENS */}
          <Snackbar
            open={cleanupInProgress}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              severity="info"
              sx={{ 
                bgcolor: colorPalette.warning,
                color: 'white',
                '& .MuiAlert-icon': { color: 'white' }
              }}
            >
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Limpiando tokens inactivos de todos los usuarios...
              </Typography>
            </Alert>
          </Snackbar>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default NotificationCreate;